import { ReactNode } from "react";

/**
 * Compact controls for settings: 32px high, 14px text, small padding, in
 * the inputs' usual border and radius (the global .input is taller).
 */
const COMPACT_FIELD =
  "rounded-md border border-gray-300 bg-white px-2 py-1 text-sm shadow-sm placeholder:text-gray-400 hover:border-gray-400 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 aria-[invalid=true]:border-red-400";

export const COMPACT_INPUT = `h-8 ${COMPACT_FIELD}`;

/** A narrow number box, for a unit right after it. */
export const COMPACT_NUMBER = `${COMPACT_INPUT} w-[4.5rem]`;

/**
 * A number box inside a sentence. 28px tall, with its margins pulling it to
 * 24px, so the line it sits on is no taller than the others and a wrapped
 * sentence keeps normal line spacing (use with leading-6 text).
 */
export const COMPACT_INLINE_NUMBER = `h-7 -my-0.5 mx-1.5 align-middle ${COMPACT_FIELD} w-[4.5rem]`;

/** A few lines of text, full width (the Booking pages bios). */
export const COMPACT_TEXTAREA = `${COMPACT_FIELD} block w-full`;

/** The short line under a box whose value is not allowed. */
export function FieldError({
  id,
  children,
}: {
  id: string;
  children: ReactNode;
}) {
  return (
    <p id={id} className="text-xs text-red-600" role="alert">
      {children}
    </p>
  );
}

/** A checkbox or radio at the same scale. */
export const COMPACT_CHECK =
  "h-4 w-4 cursor-pointer border-gray-400 text-green-600 focus:ring-green-500";

/**
 * A radio and its label, which selects it too. Side by side, not nested: a
 * global rule hides radios inside labels.
 */
export function CompactRadio({
  id,
  name,
  checked,
  onChange,
  children,
}: {
  id: string;
  name: string;
  checked: boolean;
  onChange: () => void;
  children: ReactNode;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 text-sm">
      <input
        id={id}
        name={name}
        type="radio"
        className={COMPACT_CHECK}
        checked={checked}
        onChange={onChange}
      />
      <label htmlFor={id} className="cursor-pointer">
        {children}
      </label>
    </span>
  );
}
